import type { ChatModelRunResult } from '@assistant-ui/react';
import type { ToolCall, RunStepResponse } from '../types';
import type { ArtifactReference, RunToolResultData } from '../types/events';
import type { UIEvent } from '../types/ui-events';
import { dispatchIfUIEvent } from './ui-event-dispatch';

// ─── Iteration state ─────────────────────────────────────────────────────────

/** Tracks state for a single agent iteration (reasoning → response → optional tool calls). */
export interface IterationState {
  reasoningText: string;
  responseText: string;
  /** null = still streaming, true = had tool calls (reasoning), false = final response */
  isThinking: boolean | null;
  toolCalls: Map<string, ToolCall>;
  /** Accumulated text from final_answer_delta events */
  finalAnswerText: string;
  /** True once final_answer_start received for this iteration */
  hasFinalAnswer: boolean;
}

/** Creates a fresh IterationState. */
export function createIterationState(): IterationState {
  return {
    reasoningText: '',
    responseText: '',
    isThinking: null,
    toolCalls: new Map(),
    finalAnswerText: '',
    hasFinalAnswer: false,
  };
}

/**
 * Gets or creates an iteration in the given map.
 * Shared between processEvents and handleSubAgentProgressEvent.
 */
export function getOrCreateIteration(
  iterations: Map<number, IterationState>,
  iter: number,
): IterationState {
  let state = iterations.get(iter);
  if (!state) {
    state = createIterationState();
    iterations.set(iter, state);
  }
  return state;
}

/**
 * Applies a tool_result event to the correct iteration.
 * Tries data.iteration first, then scans all iterations.
 * Shared between processEvents and handleSubAgentProgressEvent.
 */
export function applyToolResult(
  iterations: Map<number, IterationState>,
  data: RunToolResultData,
): void {
  // Try data.iteration first
  if (data.iteration != null) {
    const iter = iterations.get(data.iteration);
    if (iter) {
      const tc = iter.toolCalls.get(data.toolCallId);
      if (tc) {
        if (data.success) tc.output = data.output;
        else tc.error = data.error ?? 'Tool call failed';
        return;
      }
    }
  }
  // Fallback: scan all iterations
  for (const [, iter] of iterations) {
    const tc = iter.toolCalls.get(data.toolCallId);
    if (tc) {
      if (data.success) tc.output = data.output;
      else tc.error = data.error ?? 'Tool call failed';
      return;
    }
  }
}

// ─── Sub-agent dispatch detection ───────────────────────────────────────────

/**
 * Returns true if the tool-call input matches the sub-agent dispatch shape:
 * `{ tasks: [{ agentId: string, prompt: string, ... }] }`.
 * Used to suppress the real dispatch tool call when synthetic __subagent_spawn
 * already covers the sub-agent group display.
 */
export function isSubAgentDispatchInput(input: unknown): boolean {
  if (!input || typeof input !== 'object') return false;
  const obj = input as Record<string, unknown>;
  if (!Array.isArray(obj.tasks) || obj.tasks.length === 0) return false;
  const first = obj.tasks[0] as Record<string, unknown> | undefined;
  return !!first && typeof first.agentId === 'string';
}

// ─── Sub-agent types (co-located to avoid circular deps) ─────────────────────

export type SubAgentStatus = 'pending' | 'running' | 'completed' | 'failed';

export interface SubAgentState {
  taskId: string;
  runId: string;
  agentId: string;
  agentName: string;
  status: SubAgentStatus;
  startedAt?: number;
  durationMs?: number;
  reasoningText: string;
  responseText: string;
  toolCalls: Map<string, ToolCall>;
  toolCallCount: number;
  error?: string;
  iterations: Map<number, IterationState>;
  currentIteration: number;
  usage?: { inputTokens: number; outputTokens: number; totalTokens: number };
}

export interface SubAgentGroup {
  spawnedAt: number;
  /** The parent's `currentIteration` when this group was spawned. Used to
   *  splice the synthetic sub-agent block into the CoT at its chronological
   *  position (after the spawning iteration, before the parent's later work)
   *  instead of pinning it at the tail — see {@link buildContentWithSubAgents}. */
  spawnIteration?: number;
  mode?: string;
  // Phase 1: from subagent_invoked. Classic orchestrator sends
  // {agentId, prompt}; the Claude runtime sends {subagentType, description}.
  pendingTasks?: Array<{ agentId?: string; prompt?: string; subagentType?: string; description?: string }>;
  subAgents: Map<string, SubAgentState>; // Phase 2: populated by subagent_returned
  allComplete: boolean;
  summary?: {
    successCount: number;
    failureCount: number;
    totalDurationMs: number;
    tasks?: Array<{ taskId: string; agentId: string; agentName: string; success: boolean; durationMs: number }>;
  };
}

/**
 * Returns the existing {@link SubAgentState} for `runId`, or lazily creates one.
 *
 * The legacy lifecycle seeded the `subAgents` map up-front via
 * `subagent_invoked` → `subagent_returned`. The live agent-core runtime instead
 * streams `subagent_started` + the child's progress events directly, with no
 * `subagent_returned` beforehand. Without this lazy path those child events
 * would be dropped (no matching `SubAgentState`). Seeds identity fields from
 * whatever the triggering event knows; later `subagent_started` / `_completed`
 * events backfill a missing `agentName`.
 */
export function findOrCreateSubAgent(
  groups: SubAgentGroup[],
  runId: string,
  seed: { agentId?: string; agentName?: string; taskId?: string },
): SubAgentState {
  // A falsy runId must never become the Map key — `undefined` keys turn into
  // `__subagent_run_undefined` tool-call ids, and two of them (one per group)
  // crash assistant-ui with a duplicate-key error. Callers already guard, but
  // this is the last line of defense.
  if (!runId) runId = seed.taskId || `__missing_${groups.length}`;
  // Reuse an existing entry (e.g. one seeded by subagent_returned) — no dupes.
  for (const group of groups) {
    const existing = group.subAgents.get(runId);
    if (existing) return existing;
  }

  // Target the current "live" group (last one still open); create one if none.
  let group = [...groups].reverse().find((g) => !g.allComplete);
  if (!group) {
    group = {
      spawnedAt: Date.now(),
      subAgents: new Map(),
      allComplete: false,
    };
    groups.push(group);
  }

  const sa: SubAgentState = {
    taskId: seed.taskId ?? '',
    runId,
    agentId: seed.agentId ?? '',
    agentName: seed.agentName ?? seed.agentId ?? 'Sub-Agent',
    status: 'pending',
    reasoningText: '',
    responseText: '',
    toolCalls: new Map(),
    toolCallCount: 0,
    iterations: new Map(),
    currentIteration: 0,
  };
  group.subAgents.set(runId, sa);
  return sa;
}

/**
 * Serializes a sub-agent's accumulated state as a JSON string for
 * the tool-call `result` field.
 */
export function buildSubAgentResult(sa: SubAgentState): string {
  const toolCalls: Array<{ toolName: string; toolCallId: string; label?: string }> = [];
  for (const [, iter] of sa.iterations) {
    for (const [, tc] of iter.toolCalls) {
      toolCalls.push({ toolName: tc.toolName, toolCallId: tc.toolCallId, label: tc.label });
    }
  }
  return JSON.stringify({
    status: sa.status,
    agentName: sa.agentName,
    agentId: sa.agentId,
    reasoningText: sa.reasoningText,
    responseText: sa.responseText,
    toolCalls,
    toolCallCount: sa.toolCallCount,
    durationMs: sa.durationMs,
    error: sa.error,
    usage: sa.usage,
  });
}

// ─── Steps converters ────────────────────────────────────────────────────────

/**
 * Converts RunStepResponse[] (tool_call steps only) into the legacy ToolCall[] shape.
 */
export function stepsToToolCalls(steps: RunStepResponse[] | undefined): ToolCall[] {
  if (!steps) return [];
  return steps
    .filter((s) => s.type === 'tool_call')
    .map((s) => ({
      toolName: s.toolName!,
      toolCallId: s.toolCallId!,
      input: s.input ?? {},
      output: s.output,
      error: s.error,
      durationMs: s.durationMs,
    }));
}

/**
 * Converts ordered RunStepResponse[] into Map<number, IterationState> so that
 * buildContentFromIterations() can render a rich execution trace.
 *
 * - `reasoning` → appends to iter.reasoningText
 * - `narration` → appends to iter.responseText (or finalAnswerText if last narration)
 * - `tool_call` → adds to iter.toolCalls
 */
export function stepsToIterations(
  steps: RunStepResponse[] | undefined,
  opts?: {
    /**
     * Promote the last narration step to the final answer (default true).
     * Callers that hold the message's own persisted text (the authoritative
     * final answer) pass false so narrations stay in the chain-of-thought and
     * the persisted text renders as the answer — otherwise a run whose trace
     * ends with mid-run commentary would show that commentary as the answer
     * and drop the real one.
     */
    promoteLastNarrationToFinal?: boolean;
  },
): Map<number, IterationState> {
  if (!steps) return new Map();
  const promoteLastNarration = opts?.promoteLastNarrationToFinal ?? true;
  const iterations = new Map<number, IterationState>();

  // Determine the last narration step (which is the final answer)
  let lastNarrationIdx = -1;
  for (let i = steps.length - 1; i >= 0; i--) {
    if (steps[i].type === 'narration') {
      lastNarrationIdx = i;
      break;
    }
  }

  // Check if there are any tool calls after the last narration
  const hasToolCallAfterLastNarration = lastNarrationIdx >= 0 &&
    steps.slice(lastNarrationIdx + 1).some((s) => s.type === 'tool_call');

  for (let i = 0; i < steps.length; i++) {
    const step = steps[i];
    const iter = getOrCreateIteration(iterations, step.iteration);

    switch (step.type) {
      case 'reasoning':
        if (step.text) {
          iter.reasoningText += (iter.reasoningText ? '\n' : '') + step.text;
        }
        break;

      case 'narration':
        if (step.text) {
          // Last narration with no tool calls after it → final answer
          if (promoteLastNarration && i === lastNarrationIdx && !hasToolCallAfterLastNarration) {
            iter.hasFinalAnswer = true;
            iter.finalAnswerText += (iter.finalAnswerText ? '\n' : '') + step.text;
          } else {
            iter.isThinking = true;
            iter.responseText += (iter.responseText ? '\n' : '') + step.text;
          }
        }
        break;

      case 'tool_call': {
        iter.isThinking = true;
        const tc: ToolCall = {
          toolName: step.toolName!,
          toolDisplayName: step.toolDisplayName,
          toolCallId: step.toolCallId!,
          input: step.input ?? {},
          output: step.output,
          error: step.error,
          durationMs: step.durationMs,
          label: step.label,
        };
        iter.toolCalls.set(tc.toolCallId, tc);
        break;
      }
    }
  }

  return iterations;
}

// ─── Content builders ────────────────────────────────────────────────────────

/**
 * Builds rich content from iteration state, emitting reasoning + tool-call parts
 * for thinking iterations and text parts for the final response.
 *
 * Note: Map insertion order is used (no sort needed — iteration numbers are monotonically increasing).
 */
/** A content part tagged with the iteration it was emitted from. Lets callers
 *  (e.g. {@link buildContentWithSubAgents}) splice extra content at a precise
 *  chronological boundary without re-deriving the per-iteration emission. */
interface TaggedPart {
  iter: number;
  part: unknown;
}

/**
 * Single source of truth for turning iteration state into ordered content
 * parts, each tagged with its iteration number. {@link buildContentFromIterations}
 * and {@link buildContentWithSubAgents} both build on this so they can never
 * disagree about ordering.
 */
function buildTaggedContentFromIterations(
  iterations: Map<number, IterationState>,
  hasFinalAnswer: boolean,
  dispatchUIEvent?: (event: UIEvent) => void,
  dispatchedIds?: Set<string>,
  artifactCache?: Map<string, ArtifactReference>,
  getToolName?: (nameOrId: string) => string,
): TaggedPart[] {
  const tagged: TaggedPart[] = [];

  for (const [iterNum, iter] of iterations) {
    // Emit reasoning text if present
    if (iter.reasoningText) {
      tagged.push({ iter: iterNum, part: { type: 'reasoning' as const, text: iter.reasoningText } });
    }

    // Emit response text
    if (iter.responseText) {
      if (hasFinalAnswer || iter.isThinking !== false) {
        tagged.push({ iter: iterNum, part: { type: 'reasoning' as const, text: iter.responseText } });
      } else {
        tagged.push({ iter: iterNum, part: { type: 'text' as const, text: iter.responseText } });
      }
    }

    // Emit tool-call parts for this iteration
    for (const [, tc] of iter.toolCalls) {
      dispatchIfUIEvent(tc, dispatchUIEvent, dispatchedIds, artifactCache, getToolName);

      tagged.push({
        iter: iterNum,
        part: {
          type: 'tool-call',
          toolCallId: tc.toolCallId,
          toolName: tc.toolName,
          toolDisplayName: tc.toolDisplayName,
          args: tc.input,
          argsText: JSON.stringify(tc.input),
          result: tc.error ?? tc.output,
          isError: !!tc.error,
          durationMs: tc.durationMs,
          label: tc.label,
        },
      });
    }

    // Emit final answer text as the text part (renders below chain-of-thought)
    if (iter.finalAnswerText) {
      tagged.push({ iter: iterNum, part: { type: 'text' as const, text: iter.finalAnswerText } });
    }
  }

  return tagged;
}

export function buildContentFromIterations(
  iterations: Map<number, IterationState>,
  hasFinalAnswer: boolean,
  dispatchUIEvent?: (event: UIEvent) => void,
  dispatchedIds?: Set<string>,
  artifactCache?: Map<string, ArtifactReference>,
  getToolName?: (nameOrId: string) => string,
): ChatModelRunResult['content'] {
  const content: unknown[] = buildTaggedContentFromIterations(
    iterations,
    hasFinalAnswer,
    dispatchUIEvent,
    dispatchedIds,
    artifactCache,
    getToolName,
  ).map((t) => t.part);

  // Always emit a reasoning placeholder when there's nothing else to show —
  // keeps the ChainOfThought slot mounted between run start and the first
  // reasoning_delta so the "Thinking..." indicator doesn't flash off when an
  // early event (e.g. agent.run.started) produces an empty content array.
  if (content.length === 0) {
    content.push({ type: 'reasoning' as const, text: '' });
  }

  if (process.env.NODE_ENV === 'development') {
    const counts: Record<string, number> = {};
    for (const part of content) {
      const t = (part as { type: string }).type;
      counts[t] = (counts[t] || 0) + 1;
    }
    // Log iteration details when tool calls exist
    const toolCallNames: string[] = [];
    for (const [iterNum, iter] of iterations) {
      for (const [, tc] of iter.toolCalls) {
        toolCallNames.push(`i${iterNum}:${tc.toolName}`);
      }
    }
    console.log('[content-builder] buildContentFromIterations', {
      iterationCount: iterations.size,
      totalParts: content.length,
      partTypes: counts,
      ...(toolCallNames.length > 0 ? { toolCalls: toolCallNames } : {}),
    });
  }

  return content as ChatModelRunResult['content'];
}

/**
 * Extends buildContentFromIterations to also emit sub-agent groups as synthetic
 * tool-call parts (`__subagent_spawn` and `__subagent_run`).
 */
export function buildContentWithSubAgents(
  iterations: Map<number, IterationState>,
  subAgentGroups: SubAgentGroup[],
  hasFinalAnswer: boolean,
  dispatchUIEvent?: (event: UIEvent) => void,
  dispatchedIds?: Set<string>,
  artifactCache?: Map<string, ArtifactReference>,
  getToolName?: (nameOrId: string) => string,
): ChatModelRunResult['content'] {
  // Tagged content lets us splice the sub-agent block at a precise iteration
  // boundary further down.
  const tagged = buildTaggedContentFromIterations(
    iterations,
    hasFinalAnswer,
    dispatchUIEvent,
    dispatchedIds,
    artifactCache,
    getToolName,
  );

  // Remove real sub-agent dispatch tool calls — synthetic __subagent_spawn covers them
  const keptTagged = subAgentGroups.length > 0
    ? tagged.filter((t) => {
        const p = t.part as { type: string; args?: unknown };
        return p.type !== 'tool-call' || !isSubAgentDispatchInput(p.args);
      })
    : tagged;
  const filtered = keptTagged.map((t) => t.part);

  // Build the synthetic sub-agent tool-call parts in a separate buffer so we
  // can splice them in BEFORE any final-answer `text` part. assistant-ui's
  // chain-of-thought primitive splits the parts list at every `text`
  // boundary, so any reasoning/tool-call after a text becomes a new CoT
  // scope — appending sub-agent parts at the end produced a stray empty
  // "Thought for 0s" block after the response.
  const subAgentParts: unknown[] = [];

  for (let gi = 0; gi < subAgentGroups.length; gi++) {
    const group = subAgentGroups[gi];
    const spawnId = `__subagent_spawn_${gi}`;

    // Group header tool-call
    // Phase 1: show pendingTasks when subAgents haven't been populated yet
    // Phase 2: show full agent entries once subagent_returned has fired
    if (group.subAgents.size > 0) {
      const agents: Array<{ agentId: string; agentName: string; runId?: string; status: SubAgentStatus }> = [];
      for (const [, sa] of group.subAgents) {
        agents.push({ agentId: sa.agentId, agentName: sa.agentName, runId: sa.runId, status: sa.status });
      }
      subAgentParts.push({
        type: 'tool-call',
        toolCallId: spawnId,
        toolName: '__subagent_spawn',
        args: { count: group.subAgents.size, agents, mode: group.mode },
        argsText: JSON.stringify({ count: group.subAgents.size, agents }),
        result: group.allComplete && group.summary
          ? JSON.stringify(group.summary)
          : undefined,
        isError: false,
      });
    } else {
      // Phase 1: only pendingTasks available (no runIds yet)
      const pending = group.pendingTasks ?? [];
      const agents = pending.map((t) => ({
        agentId: t.agentId ?? t.subagentType ?? 'agent',
        prompt: t.prompt ?? t.description ?? '',
      }));
      subAgentParts.push({
        type: 'tool-call',
        toolCallId: spawnId,
        toolName: '__subagent_spawn',
        args: { count: pending.length, agents, mode: group.mode },
        argsText: JSON.stringify({ count: pending.length, agents }),
        result: undefined,
        isError: false,
      });
    }

    // Individual sub-agent tool-calls
    // Sub-agent activity data is embedded in args (not result) so that
    // assistant-ui always includes these parts in chainOfThought.parts.
    // Parts with a non-undefined `result` may be treated as "completed"
    // and excluded from the chain-of-thought, hiding live progress.
    for (const [saRunId, sa] of group.subAgents) {
      const hasProgress = sa.status === 'running' || sa.status === 'completed' || sa.status === 'failed';
      const progressData = hasProgress ? JSON.parse(buildSubAgentResult(sa)) : undefined;
      subAgentParts.push({
        type: 'tool-call',
        toolCallId: `__subagent_run_${saRunId}`,
        toolName: '__subagent_run',
        args: {
          agentId: sa.agentId,
          agentName: sa.agentName,
          runId: sa.runId,
          taskId: sa.taskId,
          status: sa.status,
          durationMs: sa.durationMs,
          toolCallCount: sa.toolCallCount,
          _progress: progressData,
        },
        argsText: JSON.stringify({
          agentId: sa.agentId,
          agentName: sa.agentName,
          status: sa.status,
        }),
        result: undefined,
        isError: false,
      });
    }
  }

  // Splice the synthetic sub-agent block at its CHRONOLOGICAL position —
  // immediately after the parent iteration in which the group spawned — rather
  // than pinning it at the tail.
  //
  // Pinning at the tail was the cause of the "stuck CoT" bug: while a run is
  // mid-flight (no final-answer `text` part yet) the block landed AFTER every
  // parent iteration, so a completed sub-agent group sat permanently at the
  // bottom of the CoT's rolling `slice(-maxItems)` window while later parent
  // activity only ever appeared in the slot just above it. Anchoring to the
  // spawn iteration puts the parent's post-spawn iterations AFTER the block, so
  // a finished group rolls off naturally as the parent keeps working. This
  // works for BOTH spawn shapes: the live `subagent_started` lifecycle (no
  // dispatch tool-call) and the legacy dispatch-tool-call path (its tool-call
  // shares the spawn iteration and is removed by `keptTagged`).
  const spawnIters = subAgentGroups
    .map((g) => g.spawnIteration)
    .filter((n): n is number => typeof n === 'number');
  const earliestSpawn = spawnIters.length > 0 ? Math.min(...spawnIters) : undefined;

  let spliceIdx: number;
  if (earliestSpawn !== undefined) {
    // First part belonging to an iteration AFTER the spawn iteration. -1 means
    // the parent hasn't produced anything past the spawn yet → append at end.
    const after = keptTagged.findIndex((t) => t.iter > earliestSpawn);
    spliceIdx = after === -1 ? filtered.length : after;
  } else {
    // No spawn-iteration info (older runs) → append at end.
    spliceIdx = filtered.length;
  }

  // Never place the block after the final-answer `text` part — assistant-ui's
  // chain-of-thought primitive splits the parts list at every `text` boundary,
  // so a block after the text becomes a stray empty "Thought for 0s" scope.
  const firstTextIdx = filtered.findIndex((p) => (p as { type: string }).type === 'text');
  if (firstTextIdx !== -1) spliceIdx = Math.min(spliceIdx, firstTextIdx);

  const content = [
    ...filtered.slice(0, spliceIdx),
    ...subAgentParts,
    ...filtered.slice(spliceIdx),
  ];

  return content as ChatModelRunResult['content'];
}

/** Warn once per id so a bad backend stream doesn't spam per-yield. */
const warnedDuplicateIds = new Set<string>();

/**
 * Deterministically de-dupes tool-call part ids so assistant-ui's
 * tapResources never sees a duplicate key (a duplicate throws and unmounts
 * the thread — the `__subagent_run_undefined` prod incident). Stable across
 * successive yields because part order is stable: the nth occurrence of an
 * id always gets the same `__dup{n}` suffix. Parts without a collision are
 * returned untouched (same references, same array when nothing changed).
 */
export function ensureUniqueToolCallIds<T>(parts: readonly T[]): readonly T[] {
  const seen = new Map<string, number>();
  let changed = false;
  const out = parts.map((part) => {
    const p = part as { type?: string; toolCallId?: string };
    if (p.type !== 'tool-call' || typeof p.toolCallId !== 'string') return part;
    const count = seen.get(p.toolCallId) ?? 0;
    seen.set(p.toolCallId, count + 1);
    if (count === 0) return part;
    if (!warnedDuplicateIds.has(p.toolCallId)) {
      warnedDuplicateIds.add(p.toolCallId);
      console.warn('[content] duplicate toolCallId rewritten', { id: p.toolCallId });
    }
    changed = true;
    return { ...part, toolCallId: `${p.toolCallId}__dup${count}` };
  });
  return changed ? out : parts;
}
